<script context="module">
  import Big from 'big.js';

  const NUMERIC = /^(\d+(\.\d*)?|\.\d+)$/i;

  function getCaretPosition(input) {
    // IE < 9 Support
    if (document.selection) {
      input.focus();
      const range = document.selection.createRange();
      const rangeLength = range.text.length;
      range.moveStart('character', -input.value.length);
      const start = range.text.length - rangeLength;
      return {start: start, end: start + rangeLength};
    }
    // IE >=9 and other browsers
    if (input.selectionStart || input.selectionStart === 0) {
      return {start: input.selectionStart, end: input.selectionEnd};
    }
    return {start: 0, end: 0};
  }

  function setCaretPosition(input, {start, end}) {
    // IE >= 9 and other browsers
    if (input.setSelectionRange) {
      input.focus();
      input.setSelectionRange(start, end);
      return;
    }

    // IE < 9
    if (input.createTextRange) {
      const range = input.createTextRange();
      range.collapse(true);
      range.moveEnd('character', end);
      range.moveStart('character', start);
      range.select();
    }
  }

  function calcValueEx(text, factor) {
    if (text === '') {
      return 0;
    }

    return Number(Big(text).times(factor));
  }

  function checkNumber(text) {
    return NUMERIC.test(text + '');
  }

  function checkPrecision(text, precision) {
    const parts = text.split('.');
    if (parts.length < 2) {
      return true;
    }

    if (precision < 1 && parts.length === 2) {
      return false;
    }

    return parts[1].length <= precision;
  }

  function checkMin(text, factor, minEx) {
    return Big(text).times(factor).gte(minEx);
  }

  function fixMin(text, factor, minEx, precision) {
    return Big(minEx).div(factor).toFixed(precision);
  }

  function checkMax(text, factor, maxEx) {
    if (maxEx === Infinity) {
      return true;
    }
    return Big(text).times(factor).lte(maxEx);
  }

  function fixMax(text, factor, maxEx, precision) {
    return Big(maxEx).div(factor).toFixed(precision);
  }

  function checkRound(text, factor, tickSizeEx) {
    const numEx = Big(text).times(factor);
    const n = numEx.div(tickSizeEx);
    return n.eq(n.round());
  }

  function fixRound(text, factor, tickSizeEx, precision) {
    return Big(text).times(factor).div(tickSizeEx).round().times(tickSizeEx).div(factor).toFixed(precision);
  }
</script>

<script>
  import {afterUpdate, createEventDispatcher} from 'svelte';
  import {numUtil} from '@/helper/num';

  const dispatch = createEventDispatcher();

  let className = '';

  export {className as class};
  export let input = null;
  export let placeholder = '';
  export let initText = '';
  export let precision = 0;
  export let factor = 1;
  export let scale = 0;

  export let valueEx = 0;
  export let tickSizeEx = 1;
  export let minEx = 0;
  export let maxEx = Infinity;
  export let disabled = false;
  export let isInline = true;
  export let operation = true;
  export let showLabel = true;
  export let height = 38;
  export let len = null;

  let hasText = false;
  let oldText = '';
  let oldPosition = null;
  let mouseDown = false;

  let isInner = false;

  $: input && handleValueAndConfigChange(valueEx, precision, scale);
  $: input && handleMaxChange(maxEx);

  afterUpdate(() => {
    isInner = false;
  });

  function handleMaxChange(maxEx) {
    if (isInner || !input) {
      return;
    }
    if (valueEx > maxEx) {
      const newValueEx = maxEx;
      const newText = numUtil.floor(newValueEx, precision, scale);
      updateInputValueFromInner(input, newValueEx, newText);
      return;
    }
  }

  function handleValueAndConfigChange(valueEx, precision, scale) {
    updateInputValueFromOuter(valueEx, precision, scale, maxEx);
  }

  function updateInputValueFromOuter(valueEx, precision, scale, maxEx) {
    if (isInner || !input) {
      return;
    }

    if (valueEx === 0) {
      setInputValue(input, initText);
      return;
    }

    if (valueEx > maxEx) {
      const newValueEx = maxEx;
      const newText = numUtil.floor(newValueEx, precision, scale);
      updateInputValueFromInner(input, newValueEx, newText);
      return;
    }

    const newText = numUtil.floor(valueEx, precision, scale);
    setInputValue(input, newText);
  }

  function updateInputValueFromInner(input, newValueEx, newText) {
    if (valueEx !== newValueEx) {
      isInner = true;
      valueEx = newValueEx;

      dispatch('value-change', newValueEx);
    }

    setInputValue(input, newText);
  }

  function setInputValue(input, newText) {
    hasText = !!newText;
    if (input && input.value !== newText) {
      input.value = newText;
    }
  }

  function decrease() {
    const newValueEx = Math.max(valueEx - tickSizeEx, minEx);
    const newText = numUtil.floor(newValueEx, precision, scale);
    updateInputValueFromInner(input, newValueEx, newText);
  }

  function increase() {
    const newValueEx = Math.min(valueEx + tickSizeEx, maxEx);
    const newText = numUtil.floor(newValueEx, precision, scale);
    updateInputValueFromInner(input, newValueEx, newText);
  }

  function handleKeyDown(e) {
    oldText = e.target.value;
    oldPosition = getCaretPosition(e.target);
    if (['ArrowDown', 'ArrowUp'].indexOf(e.key) > -1) {
      e.preventDefault();
    }
    if (e.key === 'ArrowDown') {
      decrease();
      return;
    }
    if (e.key === 'ArrowUp') {
      increase();
    }
  }

  function handleInput(e) {
    const input = e.target;
    const newText = input.value;
    if (len && newText.length > len) {
      rollback(input);
      return;
    }
    if (newText === '') {
      updateInputValueFromInner(input, 0, '');
      return;
    }

    if (!checkNumber(newText)) {
      rollback(input);
      return;
    }

    if (!checkPrecision(newText, precision)) {
      rollback(input);
      return;
    }

    if (!checkMin(newText, factor, minEx)) {
      updateInputValueFromInner(input, minEx, fixMin(newText, factor, minEx, precision));
      return;
    }

    if (!checkMax(newText, factor, maxEx)) {
      updateInputValueFromInner(input, maxEx, fixMax(newText, factor, maxEx, precision));
      return;
    }

    if (!checkRound(newText, factor, tickSizeEx)) {
      const roundText = fixRound(newText, factor, tickSizeEx, precision);
      if (!checkMin(roundText, factor, minEx)) {
        updateInputValueFromInner(input, minEx, fixMin(roundText, factor, minEx, precision));
        return;
      }
      if (!checkMax(roundText, factor, maxEx)) {
        updateInputValueFromInner(input, maxEx, fixMax(roundText, factor, maxEx));
        return;
      }
      const newValueEx = calcValueEx(roundText, factor);
      updateInputValueFromInner(input, newValueEx, roundText);
      return;
    }

    const newValueEx = calcValueEx(newText, factor);
    updateInputValueFromInner(input, newValueEx, newText);
  }

  function rollback(input) {
    setInputValue(input, oldText);
    oldPosition && setCaretPosition(input, oldPosition);
    oldPosition = null;
  }

  function handleDecrease() {
    input && input.focus();
    mouseDown = false;
    decrease();
  }

  function handleIncrease() {
    input && input.focus();
    mouseDown = false;
    increase();
  }

  function handleButtonMouseDown() {
    mouseDown = true;
  }

  function handleButtonMouseLeave() {
    mouseDown = false;
  }
</script>

<style>
  span {
    position: relative;
    display: inline-flex;
    align-items: center;
  }
  .df {
    display: flex;
  }
  span.disabled {
    pointer-events: none;
    opacity: 0.4;
  }
  input {
    flex: 1;
    border: none;
    background: transparent;
    caret-color: var(--T1);
    outline: none;
    -moz-appearance: textfield;
  }
  .bb {
    border-bottom: solid 1px #e5e5e5;
  }
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    margin: 0;
    -webkit-appearance: none;
  }
  .label {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: block;
    line-height: 38px;
    font-size: 14px;
    pointer-events: none;
    /*border-bottom: solid 1px var(--BD);*/
    transition: top 0.18s linear, line-height 0.18s linear, font-size 0.18s linear;
  }
  .label:after {
    /*background: var(--blue);*/
    position: absolute;
    right: 0;
    bottom: -1px;
    left: 0;
    height: 1px;
    transform: scaleX(0);
    content: '';
    opacity: 0;
    transition: transform 0.18s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.18s cubic-bezier(0.4, 0, 0.2, 1), -webkit-transform 0.18s cubic-bezier(0.4, 0, 0.2, 1);
  }
  input:focus + :global(.label),
  input.has-text + :global(.label) {
    top: -10px;
    line-height: 18px;
    font-size: 12px;
  }
  .mouseDown .label,
  input:focus + :global(.label) {
    color: var(--blue);
  }
  input:focus + :global(.label:after) {
    transform: scaleX(1);
    opacity: 1;
  }
  .mouseDown .label:after {
    transform: scaleX(1);
    opacity: 1;
  }
  button {
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    line-height: 24px;
    font-size: 16px;
    background: transparent;
    color: var(--blue);
    cursor: pointer;
    visibility: hidden;
  }
  button:hover {
    opacity: 0.8;
  }
  span:hover button {
    visibility: visible;
  }
  input:focus ~ button {
    visibility: visible;
  }
  button:disabled {
    color: #9fa3ab;
  }
  button:after {
    font-family: 'phemex-icon';
    content: '\e615';
  }
  button + :global(button:after) {
    content: '\e616';
  }
</style>

<span class:disabled class:mouseDown class:df={!isInline} class={className}>
  <input
    class="fw2 T2"
    placeholder={showLabel ? '' : placeholder}
    class:has-text={hasText}
    style="line-height:{height}px"
    type="text"
    bind:this={input}
    on:keydown={handleKeyDown}
    on:input={handleInput}
    on:focus
    on:blur
  />
  {#if showLabel}
    <span class="label T3">{placeholder}</span>
  {/if}
  {#if operation}
    <button disabled={valueEx - tickSizeEx < minEx} on:mousedown={handleButtonMouseDown} on:mouseleave={handleButtonMouseLeave} on:click={handleDecrease} />
    <button disabled={valueEx + tickSizeEx > maxEx} on:mousedown={handleButtonMouseDown} on:mouseleave={handleButtonMouseLeave} on:click={handleIncrease} />
  {/if}
</span>
